import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Spin, App as AntApp } from "antd";
import { queryClient } from "./utils/api.js";
import { SESSION_EXPIRED_EVENT } from "./utils/http.js";

import ThemeProvider from "./contexts/ThemeContext.js";
import PrivateRoute from "./utils/auth.js";
import RoleBasedRoute from "./utils/RoleBasedRoute.js";

// ── Eagerly loaded ──
import Logout from "./pages/Auth/Logout.js";
import ResetEmail from "./pages/Auth/ResetEmail.js";

// ── Eager layouts ──
import Dashboard from "./pages/Admin/Dashboard/Dashboard.js";
import SupportDash from "./pages/CustomerService/Dashboard/SupportDash.js";
import SalesLayout from "./pages/Sales/Layout/SalesLayout.js";
import TourAgentLayout from "./pages/TourAgent/Layout/TourAgentLayout.js";

// ── Roles ──
const adminRole = ["admin"];
const csRole = ["customer_service", "admin"];
const salesRole = ["sells_person", "admin"];
const taRole = ["tour_agent", "admin"];

// ── Lazy‑loaded pages ──
const Signin = lazy(() => import("./pages/Auth/SignIn/Signin.js"));
const Home = lazy(() => import("./pages/Admin/Home/Home.js"));
const HouseList = lazy(() => import("./pages/Admin/HouseList/HouseList.js"));
const AgentList = lazy(() => import("./pages/Admin/AgentList/AgentList.js"));
const AgentDetail = lazy(() => import("./pages/Admin/AgentList/AgentDetail.js"));
const CustomerList = lazy(() => import("./pages/Admin/CustomerList/CustomerList.js"));
const ApprovedHouse = lazy(() => import("./pages/Admin/ApprovedHouse/ApprovedHouse.js"));
const ApproveHouse = lazy(() => import("./pages/Admin/ApproveHouse/ApproveHouse.js"));
const ProcessedReserve = lazy(() => import("./pages/Admin/ProcessedReserve/ProcessedReserve.js"));
const AgentPayment = lazy(() => import("./pages/Admin/AgentPayment/AgentPayment.js"));
const ProcessedPayment = lazy(() => import("./pages/Admin/AgentPayment/ProcessedPayment.js"));
const CreateAccount = lazy(() => import("./pages/Admin/CreateAccount/CreateAccount.js"));
const Discount = lazy(() => import("./pages/Admin/Discount/Discount.js"));
const Giveaway = lazy(() => import("./pages/Admin/Giveaway/Giveaway.js"));
const EditDiscountRule = lazy(() => import("./pages/Admin/Discount/EditDiscountRule.js"));
const EditGiveaway = lazy(() => import("./pages/Admin/Giveaway/EditGiveaway.js"));
const Setting = lazy(() => import("./pages/Admin/Setting/Setting.js"));
const ChangePass = lazy(() => import("./pages/Admin/ChangePassword/ChangePass.js"));
const SystemAdmin = lazy(() => import("./pages/Admin/SystemAdmin/SystemAdmin.js"));
const SalesAdmin = lazy(() => import("./pages/Admin/SalesAdmin/SalesAdmin.js"));
const DemoRequest = lazy(() => import("./pages/Admin/DemoRequest/DemoRequest.js"));
const SubTransaction = lazy(() => import("./pages/Admin/HotelSubscription/SubscriptionTransaction.js"));
const SubActivation = lazy(() => import("./pages/Admin/HotelSubscription/SubscriptionActivation.js"));
const Modules = lazy(() => import("./pages/Admin/Subscription/Modules.js"));
const Features = lazy(() => import("./pages/Admin/Subscription/Features.js"));
const SubPlans = lazy(() => import("./pages/Admin/Subscription/Plans.js"));
const Email = lazy(() => import("./pages/Admin/Email/Email.js"));
const PushNotification = lazy(() => import("./pages/Admin/PushNotification/PushNotification.js"));
const PostBannerAds = lazy(() => import("./pages/Admin/PostBannerAds/PostBannerAds.js"));
const TourDashboard = lazy(() => import("./pages/Admin/TourManagement/TourDashboard.js"));
const TourBookings = lazy(() => import("./pages/Admin/TourManagement/TourBookings.js"));
const TourAgents = lazy(() => import("./pages/Admin/TourManagement/TourAgents.js"));
const Tours = lazy(() => import("./pages/Admin/TourManagement/Tours.js"));
const TourDisputes = lazy(() => import("./pages/Admin/TourManagement/TourDisputes.js"));
const ViewPost = lazy(() => import("./pages/Admin/Post/ViewPoster.js"));
const CustomerProfile = lazy(() => import("./pages/CustomerService/DepositHistory/CustomerProfile/CustomerProfile.js"));
const NewReservation = lazy(() => import("./pages/CustomerService/Reservation/Reservation.js"));
const SupportSetting = lazy(() => import("./pages/CustomerService/Setting/Setting.js"));
const ReservationList = lazy(() => import("./pages/CustomerService/ReserveList/ReservationList.js"));
const SalesDashboard = lazy(() => import("./pages/Sales/Dashboard/SalesDashboard.js"));
const Agents = lazy(() => import("./pages/Sales/Agents/Agents.js"));
const Houses = lazy(() => import("./pages/Sales/Houses/Houses.js"));
const SalesSetting = lazy(() => import("./pages/Sales/Setting/SalesSetting.js"));
const TourAgentDashboard = lazy(() => import("./pages/TourAgent/Dashboard/TourAgentDashboard.js"));
const AgentTourBookings = lazy(() => import("./pages/TourAgent/Booking/AgentTourBookings.js"));
const TourAgentTours = lazy(() => import("./pages/TourAgent/Tours/Tours.js"));
const CreateTour = lazy(() => import("./pages/TourAgent/Tours/CreateTour.js"));
const ViewAgentTour = lazy(() => import("./pages/TourAgent/Tours/ViewAgentTour.js"));
const Session = lazy(() => import("./pages/TourAgent/Session/Session.js"));

// ── Loading fallback ──
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
    <div className="text-center">
      <Spin size="large" />
      <p className="mt-4 text-sm text-gray-400 dark:text-gray-500 font-medium">Loading...</p>
    </div>
  </div>
);

// Wires the global "session expired" handler so a refresh failure anywhere
// in the app clears the react-query cache (preventing data leaks between
// users) before the redirect to /signin lands.
const SessionExpiredBridge = () => {
  useEffect(() => {
    const handler = () => {
      try {
        queryClient.clear();
      } catch (_) {
        /* noop */
      }
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
  }, []);
  return null;
};

const App = () => (
  <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <AntApp>
        <SessionExpiredBridge />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
            {/* ── Public ── */}
            <Route path="signin" element={<Signin />} />
            <Route path="reset-email" element={<ResetEmail />} />
            <Route path="logout" element={<Logout />} />

            {/* ── Customer Support ── */}
            <Route
              path="/customer-support"
              element={<PrivateRoute><RoleBasedRoute element={<SupportDash />} allowedRoles={csRole} /></PrivateRoute>}
            >
              <Route path="new-reservation" element={<RoleBasedRoute element={<NewReservation />} allowedRoles={csRole} />} />
              <Route path="support-setting" element={<RoleBasedRoute element={<SupportSetting />} allowedRoles={csRole} />} />
              <Route path="reserve-list" element={<RoleBasedRoute element={<ReservationList />} allowedRoles={csRole} />} />
            </Route>
            <Route
              path="/customer-Profile"
              element={<PrivateRoute><RoleBasedRoute element={<CustomerProfile />} allowedRoles={csRole} /></PrivateRoute>}
            />

            {/* ── Sales ── */}
            <Route
              path="/sales"
              element={<PrivateRoute><RoleBasedRoute element={<SalesLayout />} allowedRoles={salesRole} /></PrivateRoute>}
            >
              <Route index element={<SalesDashboard />} />
              <Route path="sales-dashboard" element={<RoleBasedRoute element={<SalesDashboard />} allowedRoles={salesRole} />} />
              <Route path="sales-agent" element={<RoleBasedRoute element={<Agents />} allowedRoles={salesRole} />} />
              <Route path="sales-house" element={<RoleBasedRoute element={<Houses />} allowedRoles={salesRole} />} />
              <Route path="sales-setting" element={<RoleBasedRoute element={<SalesSetting />} allowedRoles={salesRole} />} />
            </Route>

            {/* ── Tour Agent ── */}
            <Route
              path="/tour-agent"
              element={<PrivateRoute><RoleBasedRoute element={<TourAgentLayout />} allowedRoles={taRole} /></PrivateRoute>}
            >
              <Route index element={<TourAgentDashboard />} />
              <Route path="tour-dashboard" element={<RoleBasedRoute element={<TourAgentDashboard />} allowedRoles={taRole} />} />
              <Route path="booking" element={<RoleBasedRoute element={<AgentTourBookings />} allowedRoles={taRole} />} />
              <Route path="tours" element={<RoleBasedRoute element={<TourAgentTours />} allowedRoles={taRole} />} />
              <Route path="tours/create" element={<RoleBasedRoute element={<CreateTour />} allowedRoles={taRole} />} />
              <Route path="tours/view/:tourId" element={<RoleBasedRoute element={<ViewAgentTour />} allowedRoles={taRole} />} />
              <Route path="tours/edit/:tourId" element={<RoleBasedRoute element={<CreateTour />} allowedRoles={taRole} />} />
              <Route path="session" element={<RoleBasedRoute element={<Session />} allowedRoles={taRole} />} />
            </Route>

            {/* ── Admin (catch‑all, MUST be last) ── */}
            <Route
              path="/"
              element={<PrivateRoute><RoleBasedRoute element={<Dashboard />} allowedRoles={adminRole} /></PrivateRoute>}
            >
              <Route index element={<RoleBasedRoute element={<Home />} allowedRoles={adminRole} />} />
              <Route path="home" element={<RoleBasedRoute element={<Home />} allowedRoles={adminRole} />} />
              <Route path="house-list" element={<RoleBasedRoute element={<HouseList />} allowedRoles={adminRole} />} />
              <Route path="approved-house" element={<RoleBasedRoute element={<ApprovedHouse />} allowedRoles={adminRole} />} />
              <Route path="not-approved-house" element={<RoleBasedRoute element={<ApproveHouse />} allowedRoles={adminRole} />} />
              <Route path="agent-list" element={<RoleBasedRoute element={<AgentList />} allowedRoles={adminRole} />} />
              <Route path="agent-detail" element={<RoleBasedRoute element={<AgentDetail />} allowedRoles={adminRole} />} />
              <Route path="customer-list" element={<RoleBasedRoute element={<CustomerList />} allowedRoles={adminRole} />} />
              <Route path="proccessed-reservation" element={<RoleBasedRoute element={<ProcessedReserve />} allowedRoles={adminRole} />} />
              <Route path="agent-payment" element={<RoleBasedRoute element={<AgentPayment />} allowedRoles={adminRole} />} />
              <Route path="processed-view/:id" element={<RoleBasedRoute element={<ProcessedPayment />} allowedRoles={adminRole} />} />
              <Route path="create-account" element={<RoleBasedRoute element={<CreateAccount />} allowedRoles={adminRole} />} />
              <Route path="system-admins" element={<RoleBasedRoute element={<SystemAdmin />} allowedRoles={adminRole} />} />
              <Route path="sales-admins" element={<RoleBasedRoute element={<SalesAdmin />} allowedRoles={adminRole} />} />
              <Route path="change-password" element={<RoleBasedRoute element={<ChangePass />} allowedRoles={adminRole} />} />
              <Route path="setting" element={<RoleBasedRoute element={<Setting />} allowedRoles={adminRole} />} />
              <Route path="discount" element={<RoleBasedRoute element={<Discount />} allowedRoles={adminRole} />} />
              <Route path="giveaway" element={<RoleBasedRoute element={<Giveaway />} allowedRoles={adminRole} />} />
              <Route path="editDiscount" element={<RoleBasedRoute element={<EditDiscountRule />} allowedRoles={adminRole} />} />
              <Route path="editGiveaway" element={<RoleBasedRoute element={<EditGiveaway />} allowedRoles={adminRole} />} />
              <Route path="subscription-transaction" element={<RoleBasedRoute element={<SubTransaction />} allowedRoles={adminRole} />} />
              <Route path="subscription-activation" element={<RoleBasedRoute element={<SubActivation />} allowedRoles={adminRole} />} />
              <Route path="subscription-modules" element={<RoleBasedRoute element={<Modules />} allowedRoles={adminRole} />} />
              <Route path="subscription-plans" element={<RoleBasedRoute element={<SubPlans />} allowedRoles={adminRole} />} />
              <Route path="subscription-features/:planId" element={<RoleBasedRoute element={<Features />} allowedRoles={adminRole} />} />
              <Route path="email-config" element={<RoleBasedRoute element={<Email />} allowedRoles={adminRole} />} />
              <Route path="push-notification" element={<RoleBasedRoute element={<PushNotification />} allowedRoles={adminRole} />} />
              <Route path="post-ads" element={<RoleBasedRoute element={<PostBannerAds />} allowedRoles={adminRole} />} />
              <Route path="demo-requests" element={<RoleBasedRoute element={<DemoRequest />} allowedRoles={adminRole} />} />
              <Route path="tour-dashboard" element={<RoleBasedRoute element={<TourDashboard />} allowedRoles={adminRole} />} />
              <Route path="tour-bookings" element={<RoleBasedRoute element={<TourBookings />} allowedRoles={adminRole} />} />
              <Route path="tour-agents" element={<RoleBasedRoute element={<TourAgents />} allowedRoles={adminRole} />} />
              <Route path="tour-tours" element={<RoleBasedRoute element={<Tours />} allowedRoles={adminRole} />} />
              <Route path="tour-disputes" element={<RoleBasedRoute element={<TourDisputes />} allowedRoles={adminRole} />} />
              <Route path="view-post" element={<RoleBasedRoute element={<ViewPost />} allowedRoles={adminRole} />} />
            </Route>
          </Routes>
          </Suspense>
        </BrowserRouter>
      </AntApp>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
